import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../../styles/components/forms.css';
import "../../styles/user/MailerLite.css"

export default function MailerLite() {
    const [groups, setGroups] = useState([]);
    const [newGroupName, setNewGroupName] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [subscribers, setSubscribers] = useState([]);
    const [newSubscriber, setNewSubscriber] = useState({ email: '', name: '' });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [newAppSubscribers, setNewAppSubscribers] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [campaignData, setCampaignData] = useState({
        name: '',
        subject: '',
        fromName: '',
        fromEmail: '',
        content: '',
        selectedGroups: [],
        type: 'regular', // or 'ab' for A/B testing
        language_id: 4,  // default to Norwegian
    });
    const [pricingPlans, setPricingPlans] = useState([]);
    const [selectedTrialDuration, setSelectedTrialDuration] = useState(14); // Default 14 days
    const [trialDurationDisplay, setTrialDurationDisplay] = useState(14);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processing, setProcessing] = useState({});
    const [actionMessage, setActionMessage] = useState('');
    const [actionError, setActionError] = useState('');
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [showActionsDropdown, setShowActionsDropdown] = useState(null);
    const [userSubscriptions, setUserSubscriptions] = useState({});
    const [loadingSubscriptions, setLoadingSubscriptions] = useState({});
    const [selectedPlans, setSelectedPlans] = useState({});
    const [selectedTrialDurations, setSelectedTrialDurations] = useState({});
    const [actionMessages, setActionMessages] = useState({});
    const [selectedTierId, setSelectedTierId] = useState(null);
    const [userCreationResponse, setUserCreationResponse] = useState(null);

    useEffect(() => {
        fetchGroups();
        fetchNewAppSubscribers();
        fetchPricingPlans();
    }, []);

    const fetchGroups = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups`,
                { withCredentials: true }
            );
            setGroups(response.data.data.data);
        } catch (error) {
            setError('Kunne ikke hente grupper');
            console.error('Error fetching groups:', error);
        }
    };

    const fetchGroupSubscribers = async (groupId) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups/${groupId}/subscribers`,
                { withCredentials: true }
            );
            const subscribersWithGroup = response.data.data.data.map(subscriber => ({
                ...subscriber,
                groupId: groupId
            }));
            setSubscribers(subscribersWithGroup);
            setSelectedGroup(groupId);
        } catch (error) {
            setError('Kunne ikke hente abonnenter');
            console.error('Error fetching subscribers:', error);
        }
    };

    const createGroup = async (e) => {
        e.preventDefault();
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups`,
                { name: newGroupName },
                { withCredentials: true }
            );
            setMessage('Gruppe opprettet');
            setNewGroupName('');
            fetchGroups();
        } catch (error) {
            setError('Kunne ikke opprette gruppe');
            console.error('Error creating group:', error);
        }
    };

    const deleteGroup = async (groupId) => {
        if (window.confirm('Er du sikker på at du vil slette denne gruppen?')) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups/${groupId}`,
                    { withCredentials: true }
                );
                setMessage('Gruppe slettet');
                fetchGroups();
                if (selectedGroup === groupId) {
                    setSelectedGroup(null);
                    setSubscribers([]);
                }
            } catch (error) {
                setError('Kunne ikke slette gruppe');
                console.error('Error deleting group:', error);
            }
        }
    };

    const addSubscriber = async (e) => {
        e.preventDefault();
        if (!selectedGroup) {
            setError('Vennligst velg en gruppe først');
            return;
        }

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/subscribers`,
                {
                    ...newSubscriber,
                    groupId: selectedGroup
                },
                { withCredentials: true }
            );
            setMessage('Abonnent lagt til');
            setNewSubscriber({ email: '', name: '' });
            fetchGroupSubscribers(selectedGroup);
        } catch (error) {
            setError('Kunne ikke legge til abonnent');
            console.error('Error adding subscriber:', error);
        }
    };

    const removeSubscriber = async (email, groupId) => {
        if (window.confirm('Er du sikker på at du vil fjerne denne abonnenten?')) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups/${groupId}/subscribers/${email}`,
                    { withCredentials: true }
                );
                setMessage('Abonnent fjernet');
                fetchGroupSubscribers(groupId);
            } catch (error) {
                setError('Kunne ikke fjerne abonnent');
                console.error('Error removing subscriber:', error);
            }
        }
    };

    const fetchNewAppSubscribers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/newapp/subscribers`,
                { withCredentials: true }
            );
            setNewAppSubscribers(response.data.data);
        } catch (error) {
            setError('Kunne ikke hente nye app-abonnenter');
            console.error('Error:', error);
        }
    };

    const fetchPricingPlans = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                { withCredentials: true }
            );
            // Filter plans to only include those with tiers
            const plansWithTiers = response.data.filter(plan => plan.tiers && plan.tiers.length > 0);
            setPricingPlans(plansWithTiers);
        } catch (error) {
            console.error('Error fetching pricing plans:', error);
        }
    };

    const checkUserSubscription = async (subscribers) => {
        try {
            setLoadingSubscriptions(prev => {
                const newState = { ...prev };
                subscribers.forEach(subscriber => {
                    newState[subscriber.email] = true;
                });
                return newState;
            });

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/check-subscribers`,
                { subscribers },
                { withCredentials: true }
            );

            if (response.status === 200) {
                const results = response.data.results;
                results.forEach(result => {
                    if (result.hasUser) {
                        setUserSubscriptions(prev => ({
                            ...prev,
                            [result.email]: {
                                subscriptionId: result.subscription?.subscription_stripe_id,
                                status: result.subscription?.status,
                                trialEnd: result.subscription?.trial_end_date,
                                planName: result.subscription?.plan_name,
                                planDescription: result.subscription?.plan_description,
                                currency: result.subscription?.plan_currency,
                                amount: result.subscription?.tier_amount,
                                interval: result.subscription?.tier_interval,
                                intervalCount: result.subscription?.tier_interval_count,
                                createdAt: result.subscription?.created_at
                            }
                        }));
                    } else {
                        setUserSubscriptions(prev => ({
                            ...prev,
                            [result.email]: null
                        }));
                    }
                });
            }
        } catch (error) {
            console.error('Error checking user subscriptions:', error);
        } finally {
            setLoadingSubscriptions(prev => {
                const newState = { ...prev };
                subscribers.forEach(subscriber => {
                    newState[subscriber.email] = false;
                });
                return newState;
            });
        }
    };

    useEffect(() => {
        if (newAppSubscribers.length > 0) {
            checkUserSubscription(newAppSubscribers);
        }
    }, [newAppSubscribers]);

    const handleCreateUser = async (subscriber) => {
        // Show the modal with preselected values
        setSelectedSubscriber(subscriber);
        setSelectedPlan(selectedPlans[subscriber.email]);
        setSelectedTrialDuration(selectedTrialDurations[subscriber.email]);
        setShowPlanModal(true);
    };

    const handleConfirmCreateUser = async () => {
        if (!selectedSubscriber || !selectedPlan || !selectedTrialDuration || !selectedTierId) {
            setActionMessage('Vennligst velg en plan og prøveperiode');
            return;
        }
        if (processing[selectedSubscriber.email]) return;

        try {
            setProcessing(prev => ({ ...prev, [selectedSubscriber.email]: true }));
            setActionMessage('Oppretter bruker...');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/create-from-subscriber`,
                {
                    name: selectedSubscriber.fields?.name || 'Ingen navn',
                    email: selectedSubscriber.email,
                    phone: selectedSubscriber.fields?.phone || '',
                    trialDuration: selectedTrialDuration,
                    pricingPlanId: selectedPlan,
                    tierId: selectedTierId
                },
                { withCredentials: true }
            );

            if (response.status === 201) {
                setUserCreationResponse(response.data);
                setActionMessage('Bruker opprettet');
                checkUserSubscription([selectedSubscriber]);
            }
        } catch (error) {
            console.error('Error creating user:', error);
            setActionMessage(error.response?.data?.message || 'Kunne ikke opprette bruker');
        } finally {
            setProcessing(prev => ({ ...prev, [selectedSubscriber.email]: false }));
        }
    };

    const handleSendResetPassword = async (email) => {
        try {
            setProcessing(prev => ({ ...prev, [email]: true }));
            setActionMessage('Sender tilbakestillingslenke...');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/request-password-reset`,
                { email },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setActionMessage('Tilbakestillingslenke sendt');
            }
        } catch (error) {
            setActionMessage(error.response?.data?.message || 'Kunne ikke sende tilbakestillingslenke');
        } finally {
            setProcessing(prev => ({ ...prev, [email]: false }));
        }
    };

    const handleSendPaymentLink = async (email) => {
        if (!selectedPlan) {
            setActionMessage('Vennligst velg en prisplan først');
            return;
        }

        try {
            setProcessing(prev => ({ ...prev, [email]: true }));
            setActionMessage('Sender betalingslenke...');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/create-payment-link`,
                {
                    email,
                    pricingPlanId: selectedPlan
                },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setActionMessage('Betalingslenke sendt');
            }
        } catch (error) {
            setActionMessage(error.response?.data?.message || 'Kunne ikke sende betalingslenke');
        } finally {
            setProcessing(prev => ({ ...prev, [email]: false }));
        }
    };

    const handleCreateCampaign = async (e) => {
        e.preventDefault();
        try {
            const params = {
                name: campaignData.name,
                language_id: campaignData.language_id,
                type: campaignData.type,
                emails: [{
                    subject: campaignData.subject,
                    from_name: campaignData.fromName,
                    from: campaignData.fromEmail,
                    content: campaignData.content
                }],
                groups: campaignData.selectedGroups
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/campaigns`,
                params,
                { withCredentials: true }
            );

            setMessage('Kampanje opprettet');
            setCampaignData({
                name: '',
                subject: '',
                fromName: '',
                fromEmail: '',
                content: '',
                selectedGroups: [],
                type: 'regular',
                language_id: 4
            });
        } catch (error) {
            setError('Kunne ikke opprette kampanje');
            console.error('Error:', error);
        }
    };

    const ImageModal = ({ image, onClose }) => {
        return (
            <div className="image-modal-overlay" onClick={onClose}>
                <div className="image-modal-content" onClick={e => e.stopPropagation()}>
                    <button className="modal-close-button" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                    <img src={image.url} alt="Full størrelse" />
                    <div className="image-info">
                        <p>Lastet opp: {new Date(image.createdOn).toLocaleDateString()}</p>
                        <p>Type: {image.contentType}</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mailerlite-container">
            <div className="page-header">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Nyhetsbrev administrasjon</h1>
            </div>

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}

            <div className="mailerlite-grid">
                <div className="groups-section">
                    <h2>Nyhetsbrev grupper</h2>
                    <form onSubmit={createGroup} className="create-group-form">
                        <input
                            type="text"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            placeholder="Nytt gruppenavn"
                            required
                        />
                        <button type="submit">Opprett gruppe</button>
                    </form>

                    <div className="groups-list">
                        {groups.map(group => (
                            <div key={group.id} className="group-item">
                                <div className="group-info">
                                    <h3>{group.name}</h3>
                                    <p>Totalt abonnenter: {group.total}</p>
                                </div>
                                <div className="group-actions">
                                    <button
                                        onClick={() => fetchGroupSubscribers(group.id)}
                                        className="view-button"
                                    >
                                        Vis abonnenter
                                    </button>
                                    <button
                                        onClick={() => deleteGroup(group.id)}
                                        className="delete-button"
                                    >
                                        Slett
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {selectedGroup && (
                    <div className="subscribers-section">
                        <h2>Abonnenter</h2>
                        <form onSubmit={addSubscriber} className="add-subscriber-form">
                            <input
                                type="email"
                                value={newSubscriber.email}
                                onChange={(e) => setNewSubscriber(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="E-post"
                                required
                            />
                            <input
                                type="text"
                                value={newSubscriber.name}
                                onChange={(e) => setNewSubscriber(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Navn"
                                required
                            />
                            <button type="submit">Legg til abonnent</button>
                        </form>

                        <div className="subscribers-list">
                            {subscribers.map(subscriber => (
                                <div key={subscriber.id} className="subscriber-item">
                                    <div className="subscriber-info">
                                        {subscriber.image_url && (
                                            <div className="subscriber-image">
                                                <img src={subscriber.image_url} alt={subscriber.name} />
                                            </div>
                                        )}
                                        <div className="subscriber-details">
                                            <h4>{subscriber.name}</h4>
                                            <p>{subscriber.email}</p>
                                            {subscriber.phone && <p className="phone">{subscriber.phone}</p>}
                                            {subscriber.experience && (
                                                <p className="experience-level">
                                                    Erfaring: {subscriber.experience}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeSubscriber(subscriber.email, selectedGroup)}
                                        className="remove-button"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="newapp-section">
                <h2>Nye app-abonnenter</h2>
                
                {actionMessage && <div className="success-message">{actionMessage}</div>}
                {actionError && <div className="error-message">{actionError}</div>}

                <div className="newapp-subscribers-grid">
                    {newAppSubscribers.map(subscriber => (
                        <div key={subscriber.id} className="subscriber-card">
                            <div className="subscriber-info">
                                <h3>{subscriber.fields?.name || 'No Name'}</h3>
                                <p className="subscriber-email">{subscriber.email}</p>
                                {subscriber.fields?.phone && (
                                    <p className="subscriber-phone">{subscriber.fields.phone}</p>
                                )}
                                <p className="subscriber-date">{new Date(subscriber.subscribed_at).toLocaleDateString()}</p>
                            </div>

                            {subscriber.images && subscriber.images.length > 0 && (
                                <div className="subscriber-images">
                                    {subscriber.images.map((image, index) => (
                                        <img 
                                            key={index}
                                            src={image.url}
                                            alt={`${subscriber.fields?.name || 'Subscriber'}'s image`}
                                            className="subscriber-image"
                                            onClick={() => setSelectedImage(image)}
                                        />
                                    ))}
                                </div>
                            )}

                            {loadingSubscriptions[subscriber.email] ? (
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                    <p>Sjekker abonnementsstatus...</p>
                                </div>
                            ) : userSubscriptions[subscriber.email] ? (
                                <div className="subscription-info">
                                    <div className="status-badge success">
                                        <i className="fas fa-check-circle"></i>
                                        Bruker registrert
                                    </div>
                                    <div className="subscription-details">
                                        <p><strong>Abonnement ID:</strong> {userSubscriptions[subscriber.email].subscriptionId}</p>
                                        <p><strong>Status:</strong> {userSubscriptions[subscriber.email].status}</p>
                                        <p><strong>Prøveperiode slutter:</strong> {new Date(userSubscriptions[subscriber.email].trialEnd).toLocaleString('nb-NO', { 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}</p>
                                        <p><strong>Plan:</strong> {userSubscriptions[subscriber.email].planName}</p>
                                        <p><strong>Beskrivelse:</strong> {userSubscriptions[subscriber.email].planDescription}</p>
                                        <p><strong>Beløp:</strong> {userSubscriptions[subscriber.email].amount} {userSubscriptions[subscriber.email].currency}</p>
                                        <p><strong>Intervall:</strong> Hver {userSubscriptions[subscriber.email].intervalCount} {userSubscriptions[subscriber.email].interval}</p>
                                        <p><strong>Opprettet:</strong> {new Date(userSubscriptions[subscriber.email].createdAt).toLocaleString('nb-NO', { 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}</p>
                                    </div>
                                    <div className="action-buttons">
                                        <button
                                            onClick={() => handleSendResetPassword(subscriber.email)}
                                            disabled={processing[subscriber.email]}
                                            className="action-button reset-password"
                                        >
                                            {processing[subscriber.email] ? (
                                                <div className="spinner"></div>
                                            ) : (
                                                <>
                                                    <i className="fas fa-key"></i>
                                                    Send tilbakestillingslenke
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="action-buttons">
                                    <button 
                                        onClick={() => handleCreateUser(subscriber)}
                                        disabled={processing[subscriber.email]}
                                        className="action-button create-user"
                                    >
                                        {processing[subscriber.email] ? (
                                            <div className="spinner"></div>
                                        ) : (
                                            <>
                                                <i className="fas fa-user-plus"></i>
                                                Opprett bruker
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {selectedImage && (
                <ImageModal 
                    image={selectedImage} 
                    onClose={() => setSelectedImage(null)} 
                />
            )}

            {showPlanModal && selectedSubscriber && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        {!userCreationResponse ? (
                            <>
                                <h3>Velg plan for {selectedSubscriber.fields?.name || 'Bruker'}</h3>
                                
                                <div className="plan-selection">
                                    <label>Velg prisplan:</label>
                                    <select 
                                        value={`${selectedPlan}-${selectedTierId || ''}`}
                                        onChange={(e) => {
                                            const [planId, tierId] = e.target.value.split('-');
                                            setSelectedPlan(planId);
                                            setSelectedTierId(tierId);
                                        }}
                                        disabled={processing[selectedSubscriber.email]}
                                    >
                                        <option value="">Velg en plan</option>
                                        {pricingPlans.map(plan => (
                                            plan.tiers_mode === 1 && plan.tiers && plan.tiers.length > 0 ? (
                                                plan.tiers.map(tier => (
                                                    <option 
                                                        key={`${plan.idpricing}-${tier.id}`} 
                                                        value={`${plan.idpricing}-${tier.id}`}
                                                    >
                                                        {plan.name} - {tier.amount} NOK - {tier.interval_count} {tier.payment_interval}
                                                        {tier.interval_count > 1 ? 's' : ''}
                                                    </option>
                                                ))
                                            ) : null
                                        ))}
                                    </select>

                                    <div className="trial-duration-slider">
                                        <label>Prøveperiode: {trialDurationDisplay} dager</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="365"
                                            value={selectedTrialDuration}
                                            onChange={(e) => {
                                                setSelectedTrialDuration(Number(e.target.value));
                                                setTrialDurationDisplay(Number(e.target.value));
                                            }}
                                            className="trial-slider"
                                            disabled={processing[selectedSubscriber.email]}
                                        />
                                        <div className="trial-duration-markers">
                                            <span>1 dag</span>
                                            <span>180 dager</span>
                                            <span>365 dager</span>
                                        </div>
                                    </div>

                                    
                                </div>

                                <div className="modal-actions">
                                    <button
                                        onClick={() => {
                                            setShowPlanModal(false);
                                            setSelectedSubscriber(null);
                                            setSelectedTierId(null);
                                            setUserCreationResponse(null);
                                        }}
                                        className="modal-button cancel"
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        onClick={handleConfirmCreateUser}
                                        disabled={processing[selectedSubscriber.email] || !selectedPlan || !selectedTrialDuration || !selectedTierId}
                                        className="modal-button confirm"
                                    >
                                        {processing[selectedSubscriber.email] ? (
                                            <div className="spinner"></div>
                                        ) : (
                                            'Opprett bruker'
                                        )}
                                    </button>

                                </div>
                                {userCreationResponse !== null && <h1>{userCreationResponse.message}</h1>}
                            </>
                        ) : (
                            <div className="user-creation-success">
                                <div className="success-icon">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <h3>Bruker opprettet!</h3>
                                <div className="user-details">
                                    <div className="detail-row">
                                        <span className="label">Bruker ID:</span>
                                        <span className="value">{userCreationResponse.id}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Midlertidig passord:</span>
                                        <span className="value highlight">{userCreationResponse.tempPassword}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Abonnement ID:</span>
                                        <span className="value">{userCreationResponse.subscriptionId}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Kunde ID:</span>
                                        <span className="value">{userCreationResponse.customerId}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowPlanModal(false);
                                        setSelectedSubscriber(null);
                                        setSelectedTierId(null);
                                        setUserCreationResponse(null);
                                    }}
                                    className="modal-button confirm"
                                >
                                    Lukk
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
} 